import React, { useContext, useEffect, useRef, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { Basket, productsActions } from '../../store/products';
import { Loading, UiActions } from '../../store/ui';
import { searchActions as _searchActions, SearchLocation } from '../../store/search';
import { EInputType, GoogleApiWrapper, Input, KitIcons, ListMapToggle, parseToApiDate, SpinnerV2 } from '@repo/apg-ui';

import { getConfig } from '../../config';

import './RefineResults.scss';
import { Accordion, AccordionContext, Card, useAccordionButton } from 'react-bootstrap';
import { AppState } from '../../store';
import { Filters } from '../../components/Filters';
import { ProductTable } from '../../components/Product/ProductTable';
import { ProductFilter } from '../../store/products/types';
import { ModalContainer } from '../../components/ModalContainer';
import Search from '../../components/Search';
import BasketComponent from '../../components/Basket';
import * as _actionsBasket from '../../store/products/actions';
import MapContainer from '../../components/MapContainer';
import DisplayAll from '../../components/DisplayAll';
import { FormattedMessage, useIntl } from 'react-intl';
import { generateCriteria } from '../../utils/payloads';
import { checkNotices } from '../../utils/notices';
import { useScroll, useUpdateEffect } from 'react-use';
import classNames from 'classnames';

const placesApiKey = getConfig('placesApiKey');

const ContextAwareToggle = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);
  const isCurrentEventKey = activeEventKey === eventKey;
  return (
    <div
      className='d-flex justify-content-between align-items-center collapse-action cursor-pointer'
      onClick={decoratedOnClick}
    >
      <div>{children}</div>
      <div className='pe-4'>{isCurrentEventKey ? <KitIcons.ChevUpIcon /> : <KitIcons.ChevDownIcon />}</div>
    </div>
  );
};

interface Props {
  actions: typeof productsActions;
  actionsBasket: typeof _actionsBasket;
  uiActions: typeof UiActions;
  products: any;
  productsByGroups: any;
  productFiltersDataByUrn: IDictionary<any>;
  productFiltersByUrn: IDictionary<ProductFilter>;
  productsByUrn: any;
  searchGroupFormat: string;
  possibleBookingDates: any;
  searchLocations: SearchLocation[];
  searchActions: typeof _searchActions;
  digiflex: any;
  dispatch: Dispatch;
  dateRanges: any[];
  industry: string;
  subformats: string[];
  duration: number;
  routerLocation: any;
  isShowUnavailable: boolean;
  loading: Loading;
  industryBig5: any;
  batchSelect: any;
  basket: Basket;
  selectedFilters: any;
  searchParameters: any;
  notices: any;
  filters: any;
}

interface AddToBasketPayload {
  basketreference: any;
  basket: number;
  producturns: any[];
  startdate: string;
  autocorrectstartdate: number;
  duration: number;
  timeslot?: number;
  spotlength?: number;
  budget?: number;
  frequency?: string;
}

interface CreateBasketResponse {
  data: {
    basket: number;
    basketreference: string;
  };
}

const RefineResults = ({
  actions,
  productsByGroups,
  productFiltersDataByUrn,
  productFiltersByUrn,
  subformats,
  industryBig5,
  routerLocation,
  possibleBookingDates,
  productsByUrn,
  loading,
  uiActions,
  actionsBasket,
  basket,
  dateRanges,
  duration,
  batchSelect,
  selectedFilters,
  searchParameters,
  isShowUnavailable,
  searchActions,
  searchLocations,
  notices,
  filters,
}: Props) => {
  const [productLoadingStatus, setProductLoadingStatus] = useState({});
  const [filteredProducts, setFilteredProducts] = useState(productsByGroups);
  const [filteredProductsByUrn, setFilteredProductsByUrn] = useState(productsByUrn);
  const [mapView, setMapView] = useState<boolean>(false);
  const [displayAllOpen, setDisplayAllOpen] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const rootScrollRef = React.useRef(document.getElementById('root'));
  const stickyContainerRef = React.useRef<HTMLDivElement>();
  const resultFilterRef = React.useRef<HTMLDivElement>();
  const [isFilterSticky, setIsFilterSticky] = useState<boolean>(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [groupTitle, setGroupTitle] = useState<string>(null);
  const scrollPos = useScroll(rootScrollRef);
  const intersectionRef = React.useRef(null);
  const intl = useIntl();

  const currentAbortController = useRef(null);

  useEffect(() => {
    if (stickyContainerRef.current) {
      const elementRef = stickyContainerRef.current.getBoundingClientRect();
      if (elementRef && rootScrollRef.current.scrollTop >= elementRef.top) {
        setIsFilterSticky(true);
      } else {
        setIsFilterSticky(false);
      }

      if (rootScrollRef?.current?.scrollTop >= 80) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    }

    if (intersectionRef?.current) {
      const groups = intersectionRef?.current?.querySelectorAll("div[class^='product-group-container']");
      let currentTitle = '';
      groups.forEach((groupContainer) => {
        const rect = groupContainer.getBoundingClientRect();
        if (!loading?.general && rect.top < 265 && rect.bottom > 250) {
          currentTitle = groupContainer?.dataset?.groupname;
        }
      });

      setGroupTitle(currentTitle);
    }
  }, [scrollPos.y]);

  const handleFilterChange = (filterType: string, value: string) => {
    const updatedFilters = { ...selectedFilters };
    if (updatedFilters[filterType]) {
      if (updatedFilters[filterType].includes(value)) {
        updatedFilters[filterType] = updatedFilters[filterType].filter((v) => v !== value);
      } else {
        updatedFilters[filterType] = [...updatedFilters[filterType], value];
      }
    } else {
      updatedFilters[filterType] = [value];
    }
    actions.filteringProducts(updatedFilters);
  };

  const handleTagChange = (select: string, value: string, radiotype?: boolean) => {
    const updatedFilters = { ...selectedFilters };

    // Ensure tags object and select key exist
    if (!updatedFilters['tags']) {
      updatedFilters['tags'] = {};
    }

    if (!updatedFilters['tags'][select]) {
      updatedFilters['tags'][select] = [];
    }

    // Check if the value already exists in the array
    const valueIndex = updatedFilters['tags'][select].indexOf(value);

    if (valueIndex > -1) {
      // Value exists, so remove it
      updatedFilters['tags'][select].splice(valueIndex, 1);
    } else {
      if (radiotype) {
        updatedFilters['tags'][select] = [];
      }
      // Value does not exist, so add it
      updatedFilters['tags'][select] = [...updatedFilters['tags'][select], value];
    }

    actions.filteringProducts(updatedFilters);
  };
  const dispatch = useDispatch();
  useEffect(() => {
    if (notices && Object.keys(notices).length > 0) {
      checkNotices(notices, dispatch);
    }
  }, [notices]);

  const clearFilterByType = (filterType: string) => {
    actions.clearFilteredProductsByType(filterType);
  };

  const selectAllFilterByType = (filterType: string, values: string[]) => {
    const payload = {
      type: filterType,
      values: values,
    };
    actions.selectAllFilteredProductsByType(payload);
  };

  const createBasket = (product): Promise<CreateBasketResponse> => {
    const basketPayload = {
      name: '',
    };

    return new Promise((resolve, reject) => {
      actionsBasket
        .createBasket(basketPayload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          console.error('Error creating basket:', error);
          reject(error);
        });
    });
  };

  const setProductFilterData = async (producturn, payload) => {
    await actions.setProductFilterData(producturn, payload);
  };

  const generatePostPayload = () => {
    return {
      sort: [
        {
          column: searchParameters.currentSort,
          direction: searchParameters.sort[searchParameters.currentSort],
        },
      ],
      group: ['format', 'subformat'],
      criteria: generateCriteria(searchLocations, searchParameters),
    };
  };

  const handleAddToBasket = (basketId, basketReference, product, productUrn, callback) => {
    const productFilters = productFiltersByUrn[productUrn];
    let parsedStartDate = parseToApiDate(productFilters?.dateRanges?.[0]?.startDate || product?.startdate || startDate);

    const addToBasketPayload: AddToBasketPayload = {
      basketreference: basketReference,
      basket: basketId,
      producturns: [product.producturn],
      startdate: parsedStartDate,
      autocorrectstartdate: 1,
      duration: productFilters?.duration || product?.pricedays || duration,
      timeslot: productFilters?.timeslots?.[0] || searchParameters.timeslot || 1,
      spotlength: productFilters?.spotlength || product?.spotlength || searchParameters?.spotLength,
      budget: productFilters?.isBudgetChanged ? productFilters?.budget : undefined,
      frequency: productFilters?.isBudgetChanged ? productFilters?.frequency : undefined,
    };

    setProductLoadingStatus((prevStatus) => ({
      ...prevStatus,
      [productUrn]: true,
    }));

    return actionsBasket
      .addToBasket(addToBasketPayload)
      .then((response) => {
        if (response) {
          refreshBasketContent({ basketreference: basketReference, basket: basketId }, productUrn, callback);
        }
      })
      .catch((error) => {
        console.error('Error adding item to basket:', error);
        setProductLoadingStatus((prevStatus) => ({
          ...prevStatus,
          [productUrn]: false,
        }));
      })
      .finally(() => {
        setProductLoadingStatus((prevStatus) => ({
          ...prevStatus,
          [productUrn]: false,
        }));
      });
  };

  const addToBasket = (product, productUrn, callback?) => {
    if (basket.id == null) {
      setProductLoadingStatus((prevStatus) => ({
        ...prevStatus,
        [productUrn]: true,
      }));
      createBasket(product)
        .then((response: CreateBasketResponse) => {
          if (response && response.data.basket) {
            handleAddToBasket(response.data.basket, response.data.basketreference, product, productUrn, callback);
          }
        })
        .catch((error) => {
          console.error('Error creating basket:', error);
          setProductLoadingStatus((prevStatus) => ({
            ...prevStatus,
            [productUrn]: false,
          }));
        });
    } else {
      handleAddToBasket(basket.id, basket.reference, product, productUrn, callback);
    }
  };

  const updateBasketProductData = (productUrn, updatedData, basketEntryId) => {
    const payload: any = {
      basketreference: basket.reference,
      basket: basket.id,
      basketentry: basketEntryId,
      ...updatedData,
    };
    setProductLoadingStatus((prevStatus) => ({
      ...prevStatus,
      [productUrn]: true,
    }));

    actionsBasket
      .updateProductBasketEntries(payload)
      .then((response) => {
        if (response) {
          refreshBasketContent(
            {
              basketreference: payload.basketreference,
              basket: payload.basket,
            },
            productUrn,
          );
        }
      })
      .catch((error) => {
        console.error('Error updating item of basket:', error);
        setProductLoadingStatus((prevStatus) => ({
          ...prevStatus,
          [productUrn]: false,
        }));
      })
      .finally(() => {
        setProductLoadingStatus((prevStatus) => ({
          ...prevStatus,
          [productUrn]: false,
        }));
      });
  };

  const refreshBasketContent = (payload, productUrn = undefined, callback?) => {
    if (productUrn) {
      setProductLoadingStatus((prevStatus) => ({
        ...prevStatus,
        [productUrn]: true,
      }));
    }
    actionsBasket.getBasketContent(payload).finally(() => {
      if (productUrn) {
        setProductLoadingStatus((prevStatus) => ({
          ...prevStatus,
          [productUrn]: false,
        }));
      }

      if (callback) {
        callback();
      }
    });
  };

  useEffect(() => {
    let filteredProducts = [...productsByGroups];
    if (selectedFilters.networks && selectedFilters.networks.length > 0) {
      filteredProducts = filteredProducts
        .map((productGroup) => ({
          ...productGroup,
          subgroup: {
            ...productGroup.subgroup,
            elements: productGroup.subgroup.elements.filter((element) =>
              selectedFilters.networks.includes(element.groupvalue),
            ),
          },
        }))
        .filter((productGroup) => productGroup.subgroup.elements.length > 0);
    } else if (selectedFilters.networks.length === 0) {
      filteredProducts = [];
    }
    if (selectedFilters.tags && Object.keys(selectedFilters.tags).length > 0) {
      filteredProducts = filteredProducts
        .map((productGroup) => {
          const filteredElements = productGroup.subgroup.elements
            .map((element) => {
              const filteredProducts = element.products.filter((product) => {
                let allFiltersMatch = true;
                for (let filterKey in selectedFilters.tags) {
                  if (selectedFilters.tags[filterKey].length > 0) {
                    let product_selectfield = '';
                    filters.map((group) => {
                      if (group.title == filterKey) {
                        product_selectfield = group.selectfield;
                      }
                    });
                    const matchFound = selectedFilters.tags[filterKey].some(
                      (tag) =>
                        product.tags &&
                        product.tags[product_selectfield] &&
                        product.tags[product_selectfield].includes(tag),
                    );
                    if (!matchFound) {
                      allFiltersMatch = false;
                      break;
                    }
                  }
                }

                return allFiltersMatch;
              });

              return {
                ...element,
                products: filteredProducts,
                count: filteredProducts.length,
              };
            })
            .filter((element) => element.products.length > 0);

          return filteredElements.length > 0
            ? {
                ...productGroup,
                subgroup: {
                  ...productGroup.subgroup,
                  elements: filteredElements,
                },
              }
            : null;
        })
        .filter((productGroup) => productGroup !== null && productGroup.subgroup.elements.length > 0);
    }
    setFilteredProducts(filteredProducts);

    const filteredProductsByUrn = {};
    filteredProducts.forEach((group) => {
      group.subgroup.elements.forEach((element) => {
        element.products.forEach((product) => {
          filteredProductsByUrn[product.producturn] = product;
        });
      });
    });
    setFilteredProductsByUrn(filteredProductsByUrn);
  }, [productsByGroups, selectedFilters, filters]);

  const batchSelectOrDeselectGroup = (group) => {
    const payload = {
      groupId: group.groupvalue,
      productIds: group.products.map((product) => product.producturn),
    };
    if (!batchSelect.selectedGroupsIds.includes(group.groupvalue)) {
      actions.batchSelectGroup(payload);
    } else {
      actions.batchDeselectGroup(payload);
    }
  };

  const batchSelectOrDeselectProduct = (product) => {
    if (!batchSelect.selectedProductUrns.includes(product.producturn)) {
      actions.batchSelectProduct(product.producturn);
      uiActions.setIsBatchSelect(true);
    } else {
      actions.batchDeselectProduct(product.producturn);
      if (batchSelect.selectedProductUrns.length === 1) {
        uiActions.setIsBatchSelect(false);
      }
    }
  };

  const getSubgroupCount = (subgroupData): string => {
    if (isShowUnavailable) {
      return (
        (subgroupData.count === -1 ? '...' : subgroupData.count) +
        ' ' +
        intl.formatMessage({
          id: 'ec.components.networks',
          defaultMessage: 'Netze',
        })
      );
    } else {
      return intl.formatMessage(
        {
          id: 'ec.components.networksCountOf',
          defaultMessage: '{count} von {total} Netze',
        },
        {
          count: subgroupData.count,
          total: subgroupData?.countall,
        },
      );
    }
  };

  useEffect(() => {
    if (batchSelect.selectedProductUrns.length === 0) {
      uiActions.setIsBatchSelect(false);
    } else {
      uiActions.setIsBatchSelect(true);
    }
  }, [batchSelect.selectedGroupsIds, uiActions]);

  const isBatchSelected = (object, type = 'group') => {
    if (type === 'group') {
      return batchSelect.selectedGroupsIds.includes(object.groupvalue);
    } else {
      return batchSelect.selectedProductUrns.includes(object.producturn);
    }
  };

  const removeFromBasket = (basketEntryId, productUrn, callback?): void => {
    const payload = {
      entries: [basketEntryId],
      basketreference: basket.reference,
      basket: basket.id,
    };
    const product = basket.basketProducts.productsById[basketEntryId];
    setProductLoadingStatus((prevStatus) => ({
      ...prevStatus,
      [productUrn]: true,
    }));
    try {
      actionsBasket
        .removeFromBasket(payload)
        .then((response) => {
          if (response) {
            refreshBasketContent(
              {
                basketreference: basket.reference,
                basket: basket.id,
              },
              undefined,
              callback,
            );
          } else {
            console.error('Failed to remove item from basket:', response);
          }
          setProductLoadingStatus((prevStatus) => ({
            ...prevStatus,
            [productUrn]: false,
          }));
        })
        .catch((error) => {
          console.error('Error removing item from basket:', error);
          setProductLoadingStatus((prevStatus) => ({
            ...prevStatus,
            [product.producturn]: false,
          }));
          if (callback) {
            callback();
          }
        });
    } catch (err) {
      console.error('Error removing item from basket:', err);
      setProductLoadingStatus((prevStatus) => ({
        ...prevStatus,
        [product.producturn]: false,
      }));
    }
  };

  useEffect(() => {
    if (!filters || filters.length === 0) {
      actions.getFilters();
    }
  }, [filters]);

  useUpdateEffect(() => {
    if (searchParameters) {
      uiActions.setLoading('productList', true);
      actions.postSelect(generatePostPayload()).then((res) => {
        if (res === 'Aborting') {
          // That means some other fetch is in progress
          return;
        }
        uiActions.setLoading('productList', false);
      });
    }
  }, [searchParameters?.isShowUnavailable]);

  const clearFilters = () => {
    actions.clearFilteredProducts();
  };
  const { startDate } = dateRanges[0];

  const showAvailability = false;

  return (
    <div className='bg-gray-light w-100 d-flex'>
      <div ref={stickyContainerRef} className='d-flex flex-row w-100 py-3 justify-content-center sticky-container'>
        {showFilters && (
          <div
            className={classNames('w-20 p-5 mt-3 sticky-filter', {
              isSticky: isFilterSticky,
            })}
          >
            <Filters
              handleTagChange={(select, value, radiotype) => {
                handleTagChange(select, value, radiotype);
              }}
              selectedFilters={selectedFilters}
              clearFilters={clearFilters}
              setShowFilters={setShowFilters}
            />
            {showAvailability && (
              <div className='switch-wrapper align-items-center'>
                <div className='mb-4' style={{ fontSize: '12.75px', fontWeight: 600 }}>
                  <FormattedMessage
                    id='ec.components.notAvailable'
                    defaultMessage='Nur im gewählten Zeitraum verfügbare Netze anzeigen'
                  />
                </div>
                <div className='d-flex flex-row align-items-center me-4'>
                  <div className='me-3 fw-bold font-12' style={{ marginTop: '2px' }}>
                    <FormattedMessage id='ec.components.switch.yes' defaultMessage='ja' />
                  </div>
                  <Input
                    name={'show-unavailable'}
                    type='switch'
                    inputWrapperClass={'me-2'}
                    checked={isShowUnavailable}
                    defaultChecked={isShowUnavailable}
                    value={isShowUnavailable}
                    onChange={(name, value) => {
                      searchActions.setShowIsUnavailable(value);
                    }}
                  />
                  <div className='fw-bold font-12' style={{ marginTop: '2px', marginLeft: '2px' }}>
                    <FormattedMessage id='ec.components.switch.no' defaultMessage='nein' />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className={classNames('d-flex flex-column justify-content-start result-container', {
            'has-filter-visible': showFilters,
          })}
        >
          <div
            ref={resultFilterRef}
            className={classNames('sticky-result-filter-wrapper', {
              isSticky: isSticky,
            })}
          >
            <div className='sticky-result-filter'>
              <Search
                loading={loading}
                stickyBasketComponent={
                  <BasketComponent
                    actionsBasket={actionsBasket}
                    possibleBookingDates={possibleBookingDates}
                    addToBasket={addToBasket}
                    removeFromBasket={removeFromBasket}
                    isSticky={isSticky}
                  />
                }
              />
            </div>

            {!loading.general && (
              <div className='product-result-filter-view-wrapper'>
                <div
                  className={`product-result-filter-view ${!showFilters ? 'w-100 d-flex justify-content-between' : ''}`}
                >
                  <div className='d-flex justify-content-start'>
                    {!showFilters && !isSticky && (
                      <h4 className='filters-title d-flex flex-row gap-2'>
                        Filters
                        <div onClick={() => setShowFilters(true)}>
                          <KitIcons.EyeOffIcon />
                        </div>
                      </h4>
                    )}

                    {isSticky && !mapView && <h2 className='font-22 font-bold mb-0'>{groupTitle}</h2>}
                  </div>

                  <div className='group-view-filter position-relative'>
                    {/*<DisplayAll*/}
                    {/*    subformats={subformats}*/}
                    {/*    handleFilterChange={(*/}
                    {/*        filter,*/}
                    {/*        value,*/}
                    {/*    ) => {*/}
                    {/*        handleFilterChange(*/}
                    {/*            filter,*/}
                    {/*            value,*/}
                    {/*        );*/}
                    {/*    }}*/}
                    {/*    selectedFilters={selectedFilters}*/}
                    {/*    productsByGroups={productsByGroups}*/}
                    {/*    isOpen={displayAllOpen}*/}
                    {/*    setIsOpen={setDisplayAllOpen}*/}
                    {/*    clearFilterByType={*/}
                    {/*        clearFilterByType*/}
                    {/*    }*/}
                    {/*    selectAllFilterByType={*/}
                    {/*        selectAllFilterByType*/}
                    {/*    }*/}
                    {/*/>*/}
                    <ListMapToggle
                      setMapView={setMapView}
                      mapView={mapView}
                      labels={{
                        map: intl.formatMessage({
                          id: 'ec.components.map',
                          defaultMessage: 'Karte',
                        }),
                        list: intl.formatMessage({
                          id: 'ec.components.list',
                          defaultMessage: 'Liste',
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {loading.general && <SpinnerV2 />}

          {!loading.general && (
            <div className='product-result-wrapper'>
              {loading.productList && <SpinnerV2 fullScreen={false} relative />}
              {!loading.productList &&
                (mapView ? (
                  <div className='mapContainer'>
                    <MapContainer
                      center={{
                        lat: 46.8011,
                        lng: 8.2267,
                      }}
                      zoom={10}
                      locations={filteredProductsByUrn}
                      addToBasket={addToBasket}
                      removeFromBasket={removeFromBasket}
                    />
                  </div>
                ) : (
                  <div className='products-result-collapsible-group' ref={intersectionRef}>
                    {filteredProducts && filteredProducts.length
                      ? filteredProducts.map(({ subgroup, count, groupvalue }, index) => {
                          return (
                            <div
                              key={'group1' + index}
                              data-groupname={groupvalue}
                              className='product-group-container w-100 mb-4'
                            >
                              <div className='group-name'>
                                <h2 className='font-22 font-bold'>{groupvalue}</h2>
                                {/*<div className="network-count">{count} networks</div>*/}
                              </div>
                              {subgroup.elements.map((subgroupData, indexL2) => {
                                return (
                                  <Accordion defaultActiveKey='1' key={'subgroup-' + indexL2} className={'mt-5'}>
                                    <Card>
                                      <ContextAwareToggle eventKey='0'>
                                        <Card.Header>
                                          <div className='d-flex justify-content-between align-items-center w-100'>
                                            <div className='d-flex align-items-center header-gap'>
                                              <div className='d-flex' onClick={(event) => event.stopPropagation()}>
                                                <Input
                                                  inputType={EInputType.Checkbox}
                                                  type={'checkbox'}
                                                  inputWrapperClass={'checkbox-input-wrap'}
                                                  name={`group_${index}_subgroup_${indexL2}`}
                                                  checked={isBatchSelected(subgroupData, 'group')}
                                                  onChange={() => batchSelectOrDeselectGroup(subgroupData)}
                                                />
                                              </div>

                                              <div className='d-flex flex-column font-bold'>
                                                <span className='text-red font-19 mb-1'>{subgroupData.groupvalue}</span>
                                                <span className='font-14 font-500'>
                                                  {getSubgroupCount(subgroupData)}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </Card.Header>
                                      </ContextAwareToggle>
                                      <Accordion.Collapse eventKey='0'>
                                        <Card.Body>
                                          <ProductTable
                                            updateBasketProductData={updateBasketProductData}
                                            productsActions={actions}
                                            products={subgroupData.products}
                                            productFiltersByUrn={productFiltersByUrn}
                                            productFiltersDataByUrn={productFiltersDataByUrn}
                                            possibleBookingDates={possibleBookingDates}
                                            setProductFilterData={setProductFilterData}
                                            setBottomBarFilter={(show) => {
                                              uiActions.setIsShowBottomBar(show);
                                            }}
                                            productsByUrn={productsByUrn}
                                            getAvailabilityForDigitalProduct={(payload: any) =>
                                              actions.getAvailabilityForDigitalProduct(payload)
                                            }
                                            isLoading={productLoadingStatus}
                                            addToBasket={(product, productUrn: string) =>
                                              addToBasket(product, productUrn)
                                            }
                                            basketProducts={
                                              basket?.basketProducts ? basket.basketProducts.products : {}
                                            }
                                            basketProductsByUrn={
                                              basket?.basketProducts ? basket.basketProducts.productsByUrn : {}
                                            }
                                            setLoading={(key, value) => uiActions.setLoading(key, value)}
                                            isCalendarLoading={loading.popupCalendar}
                                            isBatchSelected={isBatchSelected}
                                            batchSelectOrDeselectProduct={batchSelectOrDeselectProduct}
                                            removeFromBasket={removeFromBasket}
                                            searchParameters={searchParameters}
                                          />
                                        </Card.Body>
                                      </Accordion.Collapse>
                                    </Card>
                                  </Accordion>
                                );
                              })}
                            </div>
                          );
                        })
                      : 'No Product available'}
                  </div>
                ))}

              <ModalContainer
                possibleBookingDates={possibleBookingDates}
                productsByUrn={productsByUrn}
                location={routerLocation}
                addToBasket={addToBasket}
                removeFromBasket={removeFromBasket}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
    routerLocation: state.router.location,
    dateRanges: state.locations.searchParameters.dateRanges,
    duration: state.locations.searchParameters.duration,
    batchSelect: state.products.batchSelect,
    selectedFilters: state.products.selectedFilters,
    searchParameters: state.locations.searchParameters,
    searchLocations: state.locations.searchLocations,
    isShowUnavailable: state.locations.searchParameters.isShowUnavailable,
    notices: state.products.notices,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(productsActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    searchActions: bindActionCreators(_searchActions as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    dispatch: dispatch,
  };
}

export default GoogleApiWrapper({ apiKey: placesApiKey })(connect(mapStateToProps, mapDispatchToProps)(RefineResults));
